import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, allManga } from '@/mocks/manga';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);
  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const mangaList = allManga.filter((m) => m.categorySlug === activeCategory);

  useEffect(() => {
    if (slug && categories.some((c) => c.slug === slug)) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    document.title = `${currentCategory.name}｜成人漫画分类浏览 — 漫境`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        `漫境${currentCategory.name}分类 — ${currentCategory.description} 浏览精选正版成人漫画作品，为您推荐最优质的${currentCategory.name}题材漫画。`
      );
    }
  }, [currentCategory]);

  return (
    <>
      <SchemaOrg type="WebPage" data={{ name: `${currentCategory.name} - 漫境`, url: `https://manga-realm.readdy.ai/category/${activeCategory}` }} />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-10">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {currentCategory.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">{currentCategory.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop="">
            {mangaList.map((manga) => (
              <Link
                key={manga.id}
                to={`/article/${manga.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-4">
                  <img
                    src={manga.coverImage}
                    alt={manga.title}
                    title={`${manga.title} - ${currentCategory.name}成人漫画`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <span className="text-background-50 text-xs font-medium bg-primary-500/90 px-2 py-1 rounded">
                      {manga.rating} ★
                    </span>
                  </div>
                  {manga.status === '已完结' && (
                    <span className="absolute top-2 left-2 bg-accent-500 text-background-950 text-xs font-semibold px-2 py-0.5 rounded">
                      已完结
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors">
                  {manga.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
                <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
                  {manga.description.slice(0, 60)}...
                </p>
                <div className="flex flex-wrap items-center gap-1 mt-2">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>

          {mangaList.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无作品，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}